import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { isHttpClientError } from "../../../lib/http/errors";
import { copyItinerary, listItineraryCopyTargets } from "../repo/itineraryRepo";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 500;

/** 管理复制目标的一次查询、一次写入及并发冲突恢复。 */
export function useItineraryCopyRuntime(options: {
  sourceItineraryId: string;
  visible: boolean;
}) {
  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState("");
  const [submittedKeyword, setSubmittedKeyword] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [selectedDemandId, setSelectedDemandId] = useState("");
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queryKey = ["itinerary", "copy-targets", submittedKeyword] as const;
  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => listItineraryCopyTargets(
      { keyword: submittedKeyword || undefined },
      signal,
    ),
    enabled: options.visible && hasSearched,
    retry: false,
    staleTime: 30_000,
  });
  const mutation = useMutation({
    mutationFn: ({ targetDemandId, replaceExisting }: { targetDemandId: string; replaceExisting: boolean }) => copyItinerary(
      options.sourceItineraryId,
      targetDemandId,
      replaceExisting,
    ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["itinerary", "list"] });
    },
    onError: (error) => {
      if (isHttpClientError(error) && error.code === "ITINERARY_COPY_TARGET_CONFLICT") {
        setSelectedDemandId("");
        void query.refetch();
      }
    },
  });

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (options.visible) return;
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
    setKeyword("");
    setSubmittedKeyword("");
    setHasSearched(false);
    setIsDebouncing(false);
    setSelectedDemandId("");
    mutation.reset();
  }, [options.visible]);

  useEffect(() => {
    if (!options.sourceItineraryId) return;
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
    setKeyword("");
    setSubmittedKeyword("");
    setHasSearched(false);
    setIsDebouncing(false);
    setSelectedDemandId("");
    mutation.reset();
  }, [options.sourceItineraryId]);

  const data = query.data ?? { items: [], total: 0, page: 1, pageSize: PAGE_SIZE };
  const errorMessage = mutation.isError
    ? isHttpClientError(mutation.error) && mutation.error.code === "ITINERARY_COPY_TARGET_CONFLICT"
      ? "该定制版本已被其他行程关联，候选列表已刷新，请重新选择。"
      : isHttpClientError(mutation.error) && mutation.error.code === "CUSTOMIZATION_ITINERARY_TARGET_INCOMPLETE"
        ? mutation.error.message
        : "复制行程失败，请稍后重试。"
    : query.isError
      ? "定制版本加载失败，请重试。"
      : "";

  return {
    keyword,
    submittedKeyword,
    hasSearched,
    pageSize: PAGE_SIZE,
    items: isDebouncing ? [] : data.items,
    total: data.total,
    selectedDemandId,
    loading: isDebouncing || (hasSearched && (query.isLoading || query.isFetching)),
    copying: mutation.isPending,
    errorMessage,
    setKeyword,
    submitSearch(value: string = keyword) {
      const normalized = value.trim();
      setKeyword(value);
      setSelectedDemandId("");
      if (mutation.isError) mutation.reset();
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
      }
      void queryClient.cancelQueries({
        queryKey: ["itinerary", "copy-targets"],
        exact: false,
      });
      if (!normalized) {
        setIsDebouncing(false);
        setSubmittedKeyword("");
        setHasSearched(false);
      } else {
        setIsDebouncing(true);
        searchTimerRef.current = setTimeout(() => {
          searchTimerRef.current = null;
          setIsDebouncing(false);
          if (normalized === submittedKeyword && hasSearched) {
            void query.refetch();
          } else {
            setSubmittedKeyword(normalized);
            setHasSearched(true);
          }
        }, SEARCH_DEBOUNCE_MS);
      }
    },
    selectDemand(demandId: string) {
      setSelectedDemandId(demandId);
      if (mutation.isError) mutation.reset();
    },
    confirm(replaceExisting = false) {
      if (!selectedDemandId) return Promise.reject(new Error("请先选择定制版本"));
      return mutation.mutateAsync({ targetDemandId: selectedDemandId, replaceExisting });
    },
    retry() {
      if (mutation.isError) mutation.reset();
      void query.refetch();
    },
  };
}
